import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { config } from '../../config';
import { useAppUpdate } from '../../hooks/useAppUpdate';
import isElectron from '../../utils/isElectron';
import UpdateBanner from './UpdateBanner';
import './AppReleaseModals.scss';

type AppReleaseModalsProps = {
  showChangelog?: boolean;
};

const fetchLocalChangelog = async (): Promise<string> => {
  const response = await fetch('./changelog.md');
  return response.text();
};

const fetchRemoteChangelog = async (url: string): Promise<string> => {
  const response = await fetch(url, {
    cache: 'no-cache',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch remote changelog: ${response.status}`);
  }
  return response.text();
};

const isChangelogPending = (): boolean =>
  isElectron() && localStorage.getItem('app-version') !== config.VERSION;

const AppReleaseModals: React.FC<AppReleaseModalsProps> = ({
  showChangelog = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [markdownData, setMarkdownData] = useState('');
  const changelogPending = showChangelog && isChangelogPending();
  const {
    checkSettled,
    showUpdate,
    availableVersion,
    progress,
    updateReady,
    error,
    installing,
    onInstall,
    onRetryCheck,
    resetUpdate,
  } = useAppUpdate();

  useEffect(() => {
    if (!showChangelog) return;

    const loadChangelog = async () => {
      try {
        if (isElectron() && window.api?.getChangelogUrl) {
          const remoteUrl = await window.api.getChangelogUrl();
          if (remoteUrl) {
            try {
              const remoteData = await fetchRemoteChangelog(remoteUrl);
              setMarkdownData(remoteData);
              return;
            } catch (loadError) {
              console.warn(
                'Failed to fetch remote changelog, falling back to local:',
                loadError,
              );
            }
          }
        }

        const localData = await fetchLocalChangelog();
        setMarkdownData(localData);
      } catch (loadError) {
        console.error('Failed to load changelog:', loadError);
      }
    };

    loadChangelog();
  }, [showChangelog]);

  useEffect(() => {
    if (!isElectron()) return;

    if (showUpdate) {
      setIsOpen(true);
      return;
    }

    if (changelogPending && checkSettled) {
      setIsOpen(true);
    }
  }, [showUpdate, changelogPending, checkSettled]);

  const showChangelogContent = showUpdate || changelogPending;

  const modalMode = useMemo(() => {
    if (showUpdate) return 'combined';
    if (changelogPending) return 'changelog';
    return null;
  }, [showUpdate, changelogPending]);

  const modalTitle = useMemo(() => {
    if (showUpdate) return "What's New";
    return 'Latest Updates';
  }, [showUpdate]);

  const onClose = () => {
    if (changelogPending) {
      localStorage.setItem('app-version', config.VERSION || '');
    }
    resetUpdate();
    setIsOpen(false);
  };

  if (!isElectron() || !isOpen || !modalMode) {
    return null;
  }

  return (
    <IonModal
      id="app-release-modal"
      isOpen={isOpen}
      className={`modal app-release-modal app-release-modal--${modalMode}`}
      onDidDismiss={onClose}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>{modalTitle}</IonTitle>
          <IonButtons slot="end">
            <IonButton color="light" onClick={onClose}>
              Close
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="app-release-content">
        {showUpdate && (
          <UpdateBanner
            error={error}
            updateReady={updateReady}
            progress={progress}
            installing={installing}
            onInstall={onInstall}
            onRetryCheck={onRetryCheck}
          />
        )}
        {showChangelogContent && markdownData && (
          <div className="app-release-changelog">
            {showUpdate && availableVersion && (
              <p className="app-release-version">
                Version {availableVersion} is ready to install.
              </p>
            )}
            {showUpdate && <hr />}
            <div className="links">
              <a
                href="https://cratehackers.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Website
              </a>
              |
              <a
                href="https://www.facebook.com/CrateHackers"
                target="_blank"
                rel="noopener noreferrer"
              >
                Facebook
              </a>
              |
              <a
                href="https://www.instagram.com/CrateHackers"
                target="_blank"
                rel="noopener noreferrer"
              >
                Instagram
              </a>
            </div>
            <ReactMarkdown>{markdownData}</ReactMarkdown>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default AppReleaseModals;
